import { useDeferredValue, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Card, FrameDef, FrameNode, FrameView } from '../api';
import { api, PATH_EXCLUDED, PATH_UNASSIGNED } from '../api';
import * as perf from '../utils/switch_perf';
import { Section } from './cards/Section';
import { SubSection } from './cards/SubSection';
import { CardRow, McpRecentContext } from './cards/CardRow';
import { CardGraph } from './cards/CardGraph';
import { AddFrameModal } from './AddFrameModal';
import { DeletedView } from './DeletedView';
import { specForKind } from '../kinds';

/**
 * A frame MECE gap — a `mece_required` node in the active frame that has
 * zero cards placed under it. Surfaced in the GapLedger column 4 (FRAME
 * GAPS section). The inline MeceBanner that used to render here was
 * removed once the GapLedger became the single home for unresolved items.
 */
export interface FrameGapInfo {
  /** Active frame id (so the click handler knows which frame to operate on). */
  frameId: string;
  /** Frame display name — shown alongside the path so the user knows context
   *  when multiple frames exist. */
  frameName: string;
  /** The empty topology node (path / label / probes). */
  node: FrameNode;
}

export interface CardsPanelProps {
  projectId: string | null;
  /** Project display name — forwarded into CardGraph for the §3.2.2
   *  share-card headline. null = unknown (graph falls back to a generic). */
  projectName?: string | null;
  /** Current project's pack id. Forwarded into AddFrameModal so the picker
   *  can flag pack-recommended templates. null = blank pack / no project. */
  projectPack?: string | null;
  cards: Card[];
  selectedId: string | null;
  highlightId: string | null;
  /** Card ids that should display the soft amber outline because the user
   *  is hovering ANOTHER card whose body wikilinks point at them. Set/
   *  cleared via `onHoverRefs` from CardRow's mouse handlers. */
  hoverHighlightIds?: ReadonlySet<string>;
  /** Mouseenter on a CardRow with body wikilinks → emits the resolved ref
   *  ids; mouseleave emits `[]`. Parent stashes them in `hoverHighlightIds`
   *  and the matching rows light up. */
  onHoverRefs?: (refs: string[]) => void;
  /** Bumps when card data changes — invalidates fetched frame views. */
  onSelect: (id: string) => void;
  onPeek: (id: string, anchor: DOMRect) => void;
  onGapClick: (frameId: string, node: FrameNode) => void;
  onGenerate: () => void;
  generating: boolean;
  /** Saved-document staleness count surfaced via SSE. >0 lights up a small
   *  badge on the Documents button so the user knows to open it; clicking
   *  through resolves the badge in the parent. */
  staleDocCount?: number;
  /** Notifies the parent whenever the active frame's MECE gap set changes —
   *  empty array when no active frame, no gaps, or no frames at all. App
   *  hands these to GapLedger so they show up in the FRAME GAPS section. */
  onFrameGapsChange?: (gaps: FrameGapInfo[]) => void;
  /** Open the history modal for a card id. Wired into broken-ref chips so
   *  clicking [[c-43]] when c-43 was deleted shows what was there. */
  onShowHistory?: (cardId: string) => void;
  /** True while the parent's `loadProjectData` is in flight. We render
   *  a placeholder skeleton instead of the body so the user doesn't see
   *  the previous project's cards (still in `cards` until the fetch
   *  resolves) under the new project's header. */
  switching?: boolean;
  /** Bumps when the SSE channel reports frames_changed in the active
   *  project (created / renamed / deleted / placement override /
   *  recompute). CardsPanel keys its frames-fetch effect on this so a
   *  mutation made in another tab — or a recompute that landed on the
   *  server — pulls in fresh topology automatically. */
  framesRevision?: number;
}

interface SectionTree {
  /** The top-level frame node (depth 0). */
  topNode: FrameNode;
  cardsAtSelf: Array<{ card: Card; overridden: boolean }>;
  subSections: Array<{
    node: FrameNode;
    cards: Array<{ card: Card; overridden: boolean }>;
    /** Even deeper nodes flatten into the sub-section for now (rare). */
  }>;
}

export function CardsPanel({
  projectId, projectName, projectPack, cards, selectedId, highlightId, hoverHighlightIds, onHoverRefs,
  onSelect, onPeek, onGapClick, onGenerate, generating,
  staleDocCount,
  onFrameGapsChange,
  onShowHistory,
  switching = false,
  framesRevision = 0,
}: CardsPanelProps) {
  perf.renderMark('CardsPanel', { cards: cards.length, switching });
  const { t } = useTranslation();
  const [frames, setFrames] = useState<FrameDef[]>([]);
  // null activeFrameId is a legitimate "Flat" lens choice now (not just an
  // empty-state). Auto-selected to the first frame on initial load below.
  const [activeFrameId, setActiveFrameId] = useState<string | null>(null);
  const [frameView, setFrameView] = useState<FrameView | null>(null);
  const [loadingFrame, setLoadingFrame] = useState(false);
  const [frameError, setFrameError] = useState<string | null>(null);
  const [showAddFrame, setShowAddFrame] = useState(false);
  // PR-A4d follow-up: default view is now 'graph' (TOPO). Graph is the
  // primary surface; the flat list / frame system is still reachable via
  // the GraphControls "≡ List" button but isn't the landing page anymore.
  const [view, setView] = useState<'cards' | 'deleted' | 'graph'>('graph');
  const [deletedCount, setDeletedCount] = useState(0);
  // Card ids touched by an external MCP client in the last 24h. Drives
  // the per-row ⚡ badge — gives the user feedback that their cards are
  // actually getting consumed by Claude Code / Cursor / etc. Empty when
  // the MCP server is offline / unused / disabled.
  const [mcpRecentIds, setMcpRecentIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  // Lens picker dropdown open state — controlled by the chip button.
  const [pickerOpen, setPickerOpen] = useState(false);
  // "View hidden" toggle. When true, cards placed in __excluded__ render in
  // a dedicated HiddenSection at the bottom so the user can drag them back.
  const [showHidden, setShowHidden] = useState(false);
  // Lens MVP: when activeFrameId === null we render FlatList. `groupBy`
  // toggles between time-reverse-chrono (`'none'`) and kind-grouped
  // (`'kind'`). `'none'` is the safest default — pure ORDER BY created_at
  // DESC, never drifts. `'kind'` adds maturity-ordered section headers
  // (decision → statement → gap → question → observation → note) so the
  // user can see "what's settled vs what's open" at a glance.
  const [groupBy, setGroupBy] = useState<'none' | 'kind'>('none');
  // Lens MVP: "Show archived" toggle. The parent's `cards` prop comes
  // from a listCards call that defaults to include_archived=false (so the
  // default Time / Status view doesn't drown in junk cards). When the
  // user flips this on, CardsPanel does its own re-fetch with
  // include_archived=true and uses the local copy. This avoids touching
  // App.tsx's seven existing listCards call sites — the toggle is local
  // to the lens.
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCards, setArchivedCards] = useState<Card[] | null>(null);
  useEffect(() => {
    if (!projectId || !showArchived) {
      setArchivedCards(null);
      return;
    }
    let cancelled = false;
    api
      .listCards(projectId, { includeArchived: true })
      .then((cs) => {
        if (!cancelled) setArchivedCards(cs);
      })
      .catch(() => {
        if (!cancelled) setArchivedCards(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, showArchived, cards]);
  // The cards array actually rendered. When showArchived is on, prefer
  // the local fetch (includes archived); otherwise use the parent's
  // already-filtered list. Then drop `kind=question` — questions moved to
  // the OPEN column (see GapLedger's OPEN QUESTIONS section). They still
  // exist in the project (wikilinks resolve, export includes them, frame
  // placements are preserved server-side) but don't render here.
  const effectiveCardsRaw = showArchived && archivedCards ? archivedCards : cards;
  // Defer the heavy 95-row list rendering so chrome (header, lens chip,
  // counts) commits in the same frame as the click. Without this, the
  // parent's `setCards(cachedCards)` on switch blocks the main thread
  // for ~250ms before ANY visual feedback — which reads to the user as
  // "硬" / jerky. With it, the click registers instantly and the list
  // streams in over the next frame or two. React 18 keeps this
  // interruptible, so a follow-up click can pre-empt the deferred
  // render.
  const deferredCardsRaw = useDeferredValue(effectiveCardsRaw);
  // GUARD: useDeferredValue returns the PREVIOUS value while React is
  // computing the new render. On a project switch, that previous value
  // is the OTHER project's cards — rendering them briefly under the
  // new project's chrome is wrong. Detect the mismatch via the
  // per-card project_id and render an empty list during the lag.
  // (When deferred catches up, project_id matches and we render
  // normally.)
  const safeDeferredCardsRaw = useMemo(() => {
    if (!projectId) return deferredCardsRaw;
    if (deferredCardsRaw.length === 0) return deferredCardsRaw;
    if (deferredCardsRaw[0].project_id === projectId) return deferredCardsRaw;
    return [];
  }, [projectId, deferredCardsRaw]);
  const effectiveCards = useMemo(
    () => safeDeferredCardsRaw.filter((c) => c.kind !== 'question'),
    [safeDeferredCardsRaw],
  );

  // Refetch deleted count whenever the cards list changes — every card
  // delete bumps `cards`, so this picks up agent-driven deletions and
  // user-driven ones alike. Cheap query (one indexed scan).
  useEffect(() => {
    if (!projectId) {
      setDeletedCount(0);
      return;
    }
    let cancelled = false;
    api
      .listDeletedCards(projectId)
      .then((rs) => {
        if (!cancelled) setDeletedCount(rs.length);
      })
      .catch(() => {
        // Pre-migration servers don't have this endpoint; fail closed at
        // 0 so the chip just says "0 deleted" rather than blowing up.
        if (!cancelled) setDeletedCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, cards]);

  // Pull the MCP recently-read set on project change. We don't aggressively
  // refresh — the set is for "consumed in the last 24h" feedback, an order
  // of magnitude more lazy than the deleted-count above. A 60s interval
  // catches new reads while the user is actively in this project; clearing
  // on unmount stops the timer.
  useEffect(() => {
    if (!projectId) {
      setMcpRecentIds(new Set<string>());
      return;
    }
    let cancelled = false;
    const refresh = () => {
      api
        .getMcpStatus(projectId)
        .then((s) => {
          if (!cancelled) setMcpRecentIds(new Set(s.recently_read_card_ids));
        })
        .catch(() => {
          if (!cancelled) setMcpRecentIds(new Set<string>());
        });
    };
    refresh();
    const interval = setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [projectId]);

  const knownIds = useMemo(
    () => new Set(cards.map((c) => c.card_id)),
    [cards],
  );

  const reloadFrames = async (selectFrameId?: string) => {
    if (!projectId) return;
    try {
      const fs = await api.listFrames(projectId);
      setFrames(fs);
      if (selectFrameId) {
        setActiveFrameId(selectFrameId);
      } else if (activeFrameId && !fs.some((f) => f.id === activeFrameId)) {
        // Active frame got deleted → fall back to flat lens (null) rather
        // than auto-jumping into another frame. Keeps a stable default
        // anchor: the time-reverse-chrono flat view.
        setActiveFrameId(null);
      }
      // Note: we deliberately do NOT auto-select the first frame on initial
      // load. The MVP lens default is `null` (time-reverse-chrono flat),
      // which is 100% deterministic and never "drifts" — the failure mode
      // that motivated the lens redesign in the first place.
    } catch (e) {
      setFrameError(`${t('cards.errLoadFrames')}: ${(e as Error).message}`);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    reloadFrames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, framesRevision]);

  // Stale-while-revalidate cache for frame views, keyed by projectId|frameId.
  // The active fetch path re-runs on every `cards` reference change
  // (covering both card mutations and the cross-project listCards swap
  // on switch), so cold-painting the loading state every time creates a
  // visible "✦ computing layout…" flash. Painting the cached view first
  // (when present) makes the switch / mutation feel instant; revalidate
  // overwrites with the fresh server view on arrival. Cleared on
  // unmount only — refs survive component re-renders but die with the
  // CardsPanel itself.
  const frameViewCacheRef = useRef<Record<string, FrameView>>({});

  // Re-fetch on frame switch / card change. Skip when no frame active —
  // CardsPanel renders the flat empty-state view in that case.
  useEffect(() => {
    if (!projectId || !activeFrameId) {
      setFrameView(null);
      return;
    }
    const key = `${projectId}|${activeFrameId}`;
    const cached = frameViewCacheRef.current[key];
    if (cached) {
      // Paint immediately. Skip the loading flash — the cached layout is
      // close enough until the revalidate lands.
      setFrameView(cached);
      setFrameError(null);
    } else {
      setLoadingFrame(true);
      setFrameError(null);
    }
    let cancelled = false;
    api.getFrame(projectId, activeFrameId)
      .then((view) => {
        if (cancelled) return;
        frameViewCacheRef.current[key] = view;
        setFrameView(view);
      })
      .catch((e) => {
        if (cancelled) return;
        setFrameError(`${t('cards.errLoadFrame')}: ${e.message}`);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingFrame(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, activeFrameId, cards]);

  // Surface this frame's MECE gaps to the parent so the GapLedger column
  // can render them in its FRAME GAPS section. We re-emit on every frameView
  // change (including switching to/away from a frame); empty when there's
  // no active frame or no gaps.
  useEffect(() => {
    if (!onFrameGapsChange) return;
    if (!frameView || !activeFrameId) {
      onFrameGapsChange([]);
      return;
    }
    const nodeByPath = new Map(frameView.frame.topology.map((n) => [n.path, n]));
    const gaps: FrameGapInfo[] = [];
    for (const path of frameView.mece_gaps) {
      const node = nodeByPath.get(path);
      if (node) {
        gaps.push({
          frameId: activeFrameId,
          frameName: frameView.frame.name,
          node,
        });
      }
    }
    onFrameGapsChange(gaps);
  }, [frameView, activeFrameId, onFrameGapsChange]);

  const sections = useMemo(
    () => {
      const t = performance.now();
      // Use deferred cards here too — buildSections + downstream Section
      // rendering is the bulk of the switch-time cost, deferring it
      // keeps the chrome responsive.
      const r = buildSections(frameView, safeDeferredCardsRaw);
      perf.mark('buildSections', { ms: Math.round((performance.now() - t) * 10) / 10, sections: r.length });
      return r;
    },
    [frameView, safeDeferredCardsRaw],
  );

  const onDropToNode = (cardId: string, nodePath: string) => {
    if (!projectId || !activeFrameId) return;
    api.overridePlacement(projectId, activeFrameId, cardId, nodePath)
      .then(() => api.getFrame(projectId, activeFrameId))
      .then(setFrameView)
      .catch((e) => setFrameError(`${t('cards.errOverride')}: ${e.message}`));
  };

  // Click the pin on an overridden CardRow → drop the override server-side
  // and refetch the frame view so the card moves to wherever the LLM/rule
  // pass places it next.
  const onResetOverride = (cardId: string) => {
    if (!projectId || !activeFrameId) return;
    api.clearPlacement(projectId, activeFrameId, cardId)
      .then(() => api.getFrame(projectId, activeFrameId))
      .then(setFrameView)
      .catch((e) => setFrameError(`${t('cards.errReset')}: ${e.message}`));
  };

  // Cards parked in __excluded__ — surfaced via the "View hidden" toggle in
  // the lens picker dropdown so users can see what was hidden and drag them
  // back into the frame. Returns count for the dropdown label even when the
  // section isn't currently rendered.
  const hiddenCount = useMemo(() => {
    if (!frameView) return 0;
    let n = 0;
    for (const p of Object.values(frameView.placements)) {
      if (p.node_path === PATH_EXCLUDED) n++;
    }
    return n;
  }, [frameView]);

  const activeFrame = useMemo(
    () => frames.find((f) => f.id === activeFrameId) ?? null,
    [frames, activeFrameId],
  );
  const gapCount = frameView?.mece_gaps.length ?? 0;

  return (
    <McpRecentContext.Provider value={mcpRecentIds}>
    <aside
      style={{
        // Cards is one of two elastic columns. Layout from left to right:
        // sidebar (220) → discussion (540–760, flexible) → cards (this,
        // flexible, min 360) → gap ledger (280, fixed). Min 360 keeps
        // card titles + 3-line body clamps readable even on a 1280 laptop.
        flex: 1,
        minWidth: 360,
        background: 'var(--paper-0)',
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* doc/12 §3.3.2 act three: one-shot guided stale demo. Appears
          the first time the project contains a decision that other
          cards depend on; clicking opens that decision for editing so
          the user personally triggers a stale ripple. Dismiss or a
          real cards_marked_stale event (App.tsx) retires it forever. */}
      <StaleDemoChip cards={cards} onPeek={onPeek} />
      {/* PR-A4d follow-up: when in graph view we suppress the
          CardsPanel-level HeaderRow entirely — GraphControls is the only
          chrome on top, so the user gets one header instead of two and
          the canvas reclaims ~45px of vertical space. The list-view
          escape hatch lives inside GraphControls now (≡ List button). */}
      {view !== 'graph' && (
      <HeaderRow
        view={view}
        cardCount={effectiveCards.length}
        deletedCount={deletedCount}
        onToggleView={() => setView((v) => (v === 'cards' ? 'deleted' : 'cards'))}
        onSwitchToGraph={() => setView('graph')}
        onLeaveGraph={() => setView('cards')}
        frames={frames}
        activeFrame={activeFrame}
        gapCount={gapCount}
        hiddenCount={hiddenCount}
        showHidden={showHidden}
        onToggleHidden={() => setShowHidden((v) => !v)}
        groupBy={groupBy}
        onPickGroup={(g) => {
          // "Pick a group" = activate the no-frame view + set the axis.
          // Batched so the dropdown closes once and the chip flips to
          // "Time" / "Status" immediately.
          setActiveFrameId(null);
          setGroupBy(g);
        }}
        showArchived={showArchived}
        onToggleShowArchived={() => setShowArchived((v) => !v)}
        onPickFrame={(id) => setActiveFrameId(id)}
        onAddFrame={() => setShowAddFrame(true)}
        onRenameFrame={async (fid, current) => {
          if (!projectId) return;
          const next = window.prompt(t('cards.renameFramePrompt'), current);
          if (!next || next.trim() === '' || next === current) return;
          try {
            await api.patchFrame(projectId, fid, { name: next.trim() });
            await reloadFrames(activeFrameId ?? undefined);
          } catch (e) {
            setFrameError(`${t('cards.errRename')}: ${(e as Error).message}`);
          }
        }}
        onDeleteFrame={async (fid) => {
          if (!projectId) return;
          if (!window.confirm(t('cards.deleteFrameConfirm', { name: fid }))) return;
          try {
            await api.deleteFrame(projectId, fid);
            // If we just deleted the active one, fall back to Flat.
            if (fid === activeFrameId) setActiveFrameId(null);
            await reloadFrames();
          } catch (e) {
            setFrameError(`${t('cards.errDelete')}: ${(e as Error).message}`);
          }
        }}
        pickerOpen={pickerOpen}
        setPickerOpen={setPickerOpen}
      />
      )}
      {view === 'deleted' && projectId ? (
        <DeletedView projectId={projectId} onClose={() => setView('cards')} />
      ) : null}
      {view === 'graph' && projectId ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <CardGraph
            projectId={projectId}
            projectName={projectName}
            onPeek={(id) => {
              if (onPeek) {
                // Graph nodes have no row-anchor; synthesize a zero
                // rect and let CardPeek's "centered when no room"
                // fallback take over.
                onPeek(id, new DOMRect(0, 0, 0, 0));
              } else {
                onSelect?.(id);
              }
            }}
            // PR-A4d follow-up: the "≡ List" escape hatch is hidden for
            // now — user feedback: the click experience to the legacy
            // flat-list view is rough, and graph mode is sticky enough
            // that nobody needs to leave it during dogfood. Re-enable
            // by passing onLeaveGraph back when list view is polished.
            // onLeaveGraph={() => setView('cards')}
          />
        </div>
      ) : null}
      {view === 'cards' && (
      <>
      {/* Body */}
      <div style={{ flex: 1, padding: '4px 0 18px', overflowY: 'auto' }}>
        {switching ? (
          <SwitchingSkeleton />
        ) : (
        <>
        {/* Bootstrap empty-state — no frames at all. Threshold banner pushes
            the user to add one once they have ≥6 cards. */}
        {frames.length === 0 && (
          <EmptyFlatView
            cards={effectiveCards}
            selectedId={selectedId}
            highlightId={highlightId}
            hoverHighlightIds={hoverHighlightIds}
            onHoverRefs={onHoverRefs}
            knownIds={knownIds}
            onSelect={onSelect}
            onPeek={onPeek}
            onAdd={() => setShowAddFrame(true)}
            onShowHistory={onShowHistory}
          />
        )}

        {/* No-frame view (Time / Status). The picker chip in the header
            already shows which axis is active and lets the user switch —
            no separate inline GROUP control needed. `effectiveCards`
            differs from `cards` only when "Show archived" is on; in that
            case CardsPanel did its own re-fetch with include_archived.
            (When frames.length === 0 the bootstrap EmptyFlatView above
            takes over to push the user toward adding a frame.) */}
        {frames.length > 0 && activeFrameId === null && (
          <FlatList
            cards={effectiveCards}
            groupBy={groupBy}
            selectedId={selectedId}
            highlightId={highlightId}
            hoverHighlightIds={hoverHighlightIds}
            onHoverRefs={onHoverRefs}
            knownIds={knownIds}
            onSelect={onSelect}
            onPeek={onPeek}
            onShowHistory={onShowHistory}
          />
        )}

        {/* Frame view */}
        {frames.length > 0 && activeFrameId && loadingFrame && (
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', padding: '12px 18px', letterSpacing: '.05em' }}>
            ✦ {t('cards.computingLayout')}
          </div>
        )}
        {frames.length > 0 && activeFrameId && frameError && (
          <div style={{ fontSize: 12, color: 'var(--red)', padding: '10px 18px' }}>
            {frameError}
          </div>
        )}
        {frames.length > 0 && activeFrameId && !loadingFrame && !frameError && sections.length === 0 && cards.length === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', padding: '14px 18px' }}>
            {t('cards.noCardsYet')}
          </div>
        )}

        {frames.length > 0 && activeFrameId && sections.map((sec, idx) => {
          const total = countCardsInSection(sec);
          const sectionGap =
            !!sec.topNode.mece_required && total === 0;
          return (
            <Section
              key={sec.topNode.path}
              index={idx + 1}
              label={sec.topNode.label}
              subtitle={sec.topNode.description}
              totalCards={total}
              isGap={sectionGap}
              onGapClick={() => activeFrameId && onGapClick(activeFrameId, sec.topNode)}
            >
              {/* Cards directly under this top-level node */}
              {sec.cardsAtSelf.length > 0 && (
                <DropZone path={sec.topNode.path} onDrop={onDropToNode}>
                  {sec.cardsAtSelf.map(({ card, overridden }) => (
                    <CardRow
                      key={card.card_id}
                      card={card}
                      overridden={overridden}
                      selected={card.card_id === selectedId}
                      flash={card.card_id === highlightId}
                      highlighted={hoverHighlightIds?.has(card.card_id) ?? false}
                      knownIds={knownIds}
                      onSelect={onSelect}
                      onPeek={onPeek}
                      onHoverRefs={onHoverRefs}
                      onShowHistory={onShowHistory}
                      onResetOverride={onResetOverride}
                    />
                  ))}
                </DropZone>
              )}
              {/* Sub-sections */}
              {sec.subSections.map((sub) => {
                const subGap = !!sub.node.mece_required && sub.cards.length === 0;
                return (
                  <DropZone key={sub.node.path} path={sub.node.path} onDrop={onDropToNode}>
                    <SubSection
                      label={sub.node.label}
                      count={sub.cards.length}
                      isGap={subGap}
                      onGapClick={() => activeFrameId && onGapClick(activeFrameId, sub.node)}
                    >
                      {sub.cards.map(({ card, overridden }) => (
                        <CardRow
                          key={card.card_id}
                          card={card}
                          overridden={overridden}
                          selected={card.card_id === selectedId}
                          flash={card.card_id === highlightId}
                          highlighted={hoverHighlightIds?.has(card.card_id) ?? false}
                          knownIds={knownIds}
                          onSelect={onSelect}
                          onPeek={onPeek}
                          onHoverRefs={onHoverRefs}
                          onShowHistory={onShowHistory}
                          onResetOverride={onResetOverride}
                        />
                      ))}
                    </SubSection>
                  </DropZone>
                );
              })}
            </Section>
          );
        })}

        {/* Cards LLM didn't place (or user chose to defer) */}
        {frames.length > 0 && activeFrameId && (
          <UnplacedSection
            frameView={frameView}
            cards={safeDeferredCardsRaw}
            selectedId={selectedId}
            highlightId={highlightId}
            hoverHighlightIds={hoverHighlightIds}
            onHoverRefs={onHoverRefs}
            knownIds={knownIds}
            onSelect={onSelect}
            onPeek={onPeek}
            onDropToNode={onDropToNode}
            onShowHistory={onShowHistory}
            onResetOverride={onResetOverride}
          />
        )}

        {/* Cards parked in __excluded__ — surfaced only when the user
            explicitly toggles "View hidden" in the lens dropdown. They
            normally stay out of sight; this is the escape hatch for
            "wait, where did c-12 go?" moments. */}
        {frames.length > 0 && activeFrameId && showHidden && (
          <HiddenSection
            frameView={frameView}
            cards={safeDeferredCardsRaw}
            selectedId={selectedId}
            highlightId={highlightId}
            hoverHighlightIds={hoverHighlightIds}
            onHoverRefs={onHoverRefs}
            knownIds={knownIds}
            onSelect={onSelect}
            onPeek={onPeek}
            onDropToNode={onDropToNode}
            onShowHistory={onShowHistory}
            onResetOverride={onResetOverride}
          />
        )}
        </>
        )}
      </div>

      <GenerateFooter
        projectId={projectId}
        cardCount={cards.length}
        onGenerate={onGenerate}
        generating={generating}
        staleDocCount={staleDocCount ?? 0}
      />
      </>
      )}

      {showAddFrame && projectId && (
        <AddFrameModal
          projectId={projectId}
          cardCount={cards.length}
          projectPack={projectPack ?? null}
          onClose={() => setShowAddFrame(false)}
          onCreated={async (fid) => {
            setShowAddFrame(false);
            await reloadFrames(fid);
          }}
        />
      )}
    </aside>
    </McpRecentContext.Provider>
  );
}

// ── sub-components ────────────────────────────────────────────────

/**
 * Placeholder rendered while the parent's `loadProjectData` is in flight.
 * The previous project's `cards` array is still in memory at this point,
 * so without this branch the user sees project-A's cards under project-B's
 * header for the duration of the threads+cards round-trip. Four ghost
 * rows mimic CardRow's geometry (padding, 3px left rule, 4px radius);
 * the existing `pulse` keyframe (tokens.css) gives the soft fade with a
 * stagger so it reads as "loading" rather than "broken".
 */
function SwitchingSkeleton() {
  // Heights vary slightly so the skeleton doesn't look like a striped
  // pattern — closer to the natural variance of real card titles + body
  // clamps.
  const heights = [68, 56, 76, 60];
  return (
    <div style={{ padding: '14px 18px 0' }}>
      {heights.map((h, i) => (
        <div
          key={i}
          style={{
            height: h,
            margin: '0 0 10px',
            background: 'var(--paper-1)',
            borderLeft: '3px solid var(--rule)',
            borderRadius: 4,
            opacity: 0.6,
            animation: 'pulse 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.12}s`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * The single 45px header row that replaces the old two-row Header +
 * FrameTabs stack. A frame is a *lens* — a way to view the same card
 * pool — so the picker is a dropdown with "All cards" as a first-class
 * option, not a tab bar that implies parallel contexts.
 *
 * Layout (left → right):
 *   [▾ <FrameName ✦>] · 87 · 2 gaps    ........    [3 deleted →]  ↻
 *
 * In `view='deleted'`, the dropdown collapses to a "← back" affordance —
 * deleted is a temporary side-trip, not a lens. The chip's identity
 * (frame name) is preserved across that round-trip.
 */
function HeaderRow({
  view, cardCount, deletedCount, onToggleView,
  onSwitchToGraph, onLeaveGraph,
  frames, activeFrame, gapCount, hiddenCount,
  showHidden, onToggleHidden,
  groupBy, onPickGroup,
  showArchived, onToggleShowArchived,
  onPickFrame, onAddFrame, onRenameFrame, onDeleteFrame,
  pickerOpen, setPickerOpen,
}: {
  view: 'cards' | 'deleted' | 'graph';
  cardCount: number;
  deletedCount: number;
  onToggleView: () => void;
  /** doc/11 PR-A4a — switch the body to graph view. Visible while in
   *  cards view as a small "Graph" pill. */
  onSwitchToGraph: () => void;
  /** Return from graph view back to the list. Rendered as a back
   *  button when in graph view. */
  onLeaveGraph: () => void;
  frames: FrameDef[];
  activeFrame: FrameDef | null;
  gapCount: number;
  hiddenCount: number;
  showHidden: boolean;
  onToggleHidden: () => void;
  /** Sub-axis for the no-frame view. Time = 'none', Status = 'kind'. */
  groupBy: 'none' | 'kind';
  /** Pick "All cards" with a specific groupBy in one click. */
  onPickGroup: (g: 'none' | 'kind') => void;
  /** Lens MVP: "Show archived" toggle. When on, archived cards are
   *  refetched and rendered alongside live cards (with a faded style). */
  showArchived: boolean;
  onToggleShowArchived: () => void;
  onPickFrame: (id: string | null) => void;
  onAddFrame: () => void;
  onRenameFrame: (frameId: string, currentName: string) => void;
  onDeleteFrame: (frameId: string) => void;
  pickerOpen: boolean;
  setPickerOpen: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  // doc/11 PR-A4a — graph-view header: minimal back nav + count. Skip
  // the lens picker; lens is "how to slice the list" and that semantic
  // doesn't apply to a graph.
  if (view === 'graph') {
    return (
      <div
        style={{
          height: 45,
          padding: '0 18px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <button
          onClick={onLeaveGraph}
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.06em',
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            cursor: 'pointer',
          }}
        >
          ← {t('common.back')}
        </button>
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            color: 'var(--ink-0)',
            letterSpacing: '.1em',
            fontWeight: 700,
          }}
        >
          GRAPH
        </div>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          · {cardCount}
        </span>
        <span style={{ flex: 1 }} />
      </div>
    );
  }

  // Deleted-view header: minimal back nav + count. Skip the lens picker
  // entirely — there's no lens semantics in the trash.
  if (view === 'deleted') {
    return (
      <div
        style={{
          height: 45,
          padding: '0 18px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <button
          onClick={onToggleView}
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.06em',
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            cursor: 'pointer',
          }}
        >
          ← {t('common.back')}
        </button>
        <div
          className="mono"
          style={{ fontSize: 10.5, color: 'var(--ink-0)', letterSpacing: '.1em', fontWeight: 700 }}
        >
          {t('cards.deleted')}
        </div>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          · {deletedCount}
        </span>
        <span style={{ flex: 1 }} />
      </div>
    );
  }

  // Chip label reflects the *current view* — "All cards" was confusing
  // because flat-time and flat-by-kind are now both surfaced as first-class
  // options inside the dropdown. Now the chip reads "Time" / "Status" /
  // "<FrameName>" so the user can tell at a glance what they're looking at
  // without opening the dropdown.
  const chipLabel = activeFrame
    ? activeFrame.name
    : groupBy === 'kind'
    ? t('cards.lensStatus')
    : t('cards.lensTime');
  const llmMode = activeFrame?.mode === 'llm';

  return (
    <div
      style={{
        position: 'relative',
        height: 45,
        padding: '0 14px 0 12px',
        borderBottom: '1px solid var(--rule)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
      }}
    >
      {/* Lens picker chip */}
      <button
        onClick={() => setPickerOpen(!pickerOpen)}
        className="mono"
        title={activeFrame?.description ?? t('cards.noLensHint')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11.5,
          padding: '5px 10px 5px 8px',
          maxWidth: 280,
          background: pickerOpen ? 'var(--paper-1)' : 'white',
          color: 'var(--ink-0)',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          fontWeight: 600,
          letterSpacing: '.02em',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ color: 'var(--ink-3)', fontSize: 10 }}>▾</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {chipLabel}
        </span>
        {llmMode && (
          <span
            style={{ fontSize: 9, color: 'var(--blue)', marginLeft: 1 }}
            title={t('cards.llmCategorized')}
          >
            ✦
          </span>
        )}
      </button>

      {/* Meta — count + gap count, mono micro */}
      <span
        className="mono"
        style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.04em', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        · {cardCount}
      </span>
      {activeFrame && gapCount > 0 && (
        <span
          className="mono"
          title={t('cards.gapCountHint')}
          style={{
            fontSize: 10,
            color: 'var(--amber)',
            letterSpacing: '.04em',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          · {t('cards.gapCount', { count: gapCount })}
        </span>
      )}

      <span style={{ flex: 1 }} />

      {/* doc/11 PR-A4a: open knowledge graph view. Sits alongside the
          deleted-count toggle since both are alternate views of the
          same project's cards. */}
      <button
        onClick={onSwitchToGraph}
        className="mono"
        title="See cards as a knowledge graph"
        style={{
          fontSize: 10,
          color: 'var(--ink-2)',
          letterSpacing: '.06em',
          padding: '2px 8px',
          border: '1px solid var(--rule)',
          borderRadius: 3,
          background: 'white',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        ◇ Graph
      </button>
      {deletedCount > 0 && (
        <button
          onClick={onToggleView}
          className="mono"
          title={t('cards.seeDeletedHint')}
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.06em',
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {t('cards.deletedCount', { count: deletedCount })} →
        </button>
      )}
      {/* Refresh button removed — the lens is event-driven (SSE) and
          never relies on a stale snapshot, so the manual nudge is just
          visual noise. If a sync gap shows up, fix the missing event
          rather than re-adding the button. */}

      {pickerOpen && (
        <LensDropdown
          frames={frames}
          activeFrameId={activeFrame?.id ?? null}
          groupBy={groupBy}
          hiddenCount={hiddenCount}
          showHidden={showHidden}
          showArchived={showArchived}
          onToggleHidden={() => {
            onToggleHidden();
            setPickerOpen(false);
          }}
          onToggleShowArchived={() => {
            onToggleShowArchived();
            setPickerOpen(false);
          }}
          onPick={(id) => {
            onPickFrame(id);
            setPickerOpen(false);
          }}
          onPickGroup={(g) => {
            onPickGroup(g);
            setPickerOpen(false);
          }}
          onAdd={() => {
            onAddFrame();
            setPickerOpen(false);
          }}
          onRename={onRenameFrame}
          onDelete={onDeleteFrame}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * The lens-picker dropdown. Anchored under the chip on the left edge.
 * Closes on outside click via a transparent backdrop sibling.
 */
function LensDropdown({
  frames, activeFrameId, groupBy, hiddenCount, showHidden, showArchived,
  onToggleHidden, onToggleShowArchived,
  onPick, onPickGroup, onAdd, onRename, onDelete, onClose,
}: {
  frames: FrameDef[];
  activeFrameId: string | null;
  /** Sub-axis for the no-frame ("All cards") view. Ignored when a frame
   *  is active (frame topology drives layout instead). */
  groupBy: 'none' | 'kind';
  hiddenCount: number;
  showHidden: boolean;
  /** Lens MVP: when true, the cards list re-fetches with
   *  include_archived=true so soft-hidden cards appear. */
  showArchived: boolean;
  onToggleHidden: () => void;
  onToggleShowArchived: () => void;
  onPick: (id: string | null) => void;
  /** Pick "All cards" with a specific groupBy. Equivalent to onPick(null)
   *  + setGroupBy(g), batched so the menu closes once. */
  onPickGroup: (g: 'none' | 'kind') => void;
  onAdd: () => void;
  onRename: (frameId: string, currentName: string) => void;
  onDelete: (frameId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {/* Click-outside backdrop. Transparent — but catches clicks anywhere
          on the page so the dropdown closes naturally. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
          background: 'transparent',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: 12,
          zIndex: 50,
          marginTop: 4,
          minWidth: 260,
          maxWidth: 360,
          background: 'white',
          border: '1px solid var(--rule)',
          borderRadius: 6,
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
          padding: 4,
        }}
      >
        {/* Default views — pure SQL, no LLM, never drift. */}
        <FrameMenuItem
          label={t('cards.lensTime')}
          subtitle={t('cards.lensTimeSubtitle')}
          checked={activeFrameId === null && groupBy === 'none'}
          onPick={() => onPickGroup('none')}
        />
        <FrameMenuItem
          label={t('cards.lensStatus')}
          subtitle={t('cards.lensStatusSubtitle')}
          checked={activeFrameId === null && groupBy === 'kind'}
          onPick={() => onPickGroup('kind')}
        />
        {frames.length > 0 && (
          <div style={{ borderTop: '1px solid var(--rule)', margin: '4px 0' }} />
        )}
        {frames.map((f) => (
          <FrameMenuItem
            key={f.id}
            label={f.name}
            llm={f.mode === 'llm'}
            subtitle={f.description || undefined}
            checked={activeFrameId === f.id}
            onPick={() => onPick(f.id)}
            onRename={() => onRename(f.id, f.name)}
            onDelete={() => onDelete(f.id)}
          />
        ))}
        <div style={{ borderTop: '1px solid var(--rule)', margin: '4px 0' }} />
        {hiddenCount > 0 && activeFrameId !== null && (
          <button
            onClick={onToggleHidden}
            className="mono"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              textAlign: 'left',
              fontSize: 11,
              padding: '6px 10px',
              background: 'transparent',
              border: 'none',
              borderRadius: 3,
              cursor: 'pointer',
              letterSpacing: '.04em',
              color: 'var(--ink-2)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-1)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <span style={{ width: 14, color: showHidden ? 'var(--blue)' : 'var(--ink-4)' }}>
              {showHidden ? '☑' : '☐'}
            </span>
            {t('cards.viewHidden', { count: hiddenCount })}
          </button>
        )}
        {/* Lens MVP: "Show archived" — global, applies to any view. Soft
            hidden cards re-fetched and rendered alongside live ones. */}
        <button
          onClick={onToggleShowArchived}
          className="mono"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            textAlign: 'left',
            fontSize: 11,
            padding: '6px 10px',
            background: 'transparent',
            border: 'none',
            borderRadius: 3,
            cursor: 'pointer',
            letterSpacing: '.04em',
            color: 'var(--ink-2)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-1)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          title={t('cards.showArchivedHint')}
        >
          <span style={{ width: 14, color: showArchived ? 'var(--blue)' : 'var(--ink-4)' }}>
            {showArchived ? '☑' : '☐'}
          </span>
          {t('cards.showArchived')}
        </button>
        <button
          onClick={onAdd}
          className="mono"
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            fontSize: 11,
            padding: '6px 10px',
            background: 'transparent',
            border: 'none',
            borderRadius: 3,
            cursor: 'pointer',
            letterSpacing: '.04em',
            color: 'var(--ink-1)',
            fontWeight: 600,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-1)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          + {t('cards.addFrameMenu')}
        </button>
      </div>
    </>
  );
}

function FrameMenuItem({
  label, subtitle, llm, checked, onPick, onRename, onDelete,
}: {
  label: string;
  subtitle?: string;
  llm?: boolean;
  checked: boolean;
  onPick: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'flex', alignItems: 'stretch' }}
    >
      <button
        onClick={onPick}
        className="mono"
        title={subtitle}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textAlign: 'left',
          fontSize: 11.5,
          padding: '7px 10px',
          background: 'transparent',
          border: 'none',
          borderRadius: 3,
          cursor: 'pointer',
          color: 'var(--ink-0)',
          fontWeight: checked ? 700 : 500,
          letterSpacing: '.02em',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-1)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <span style={{ width: 14, color: checked ? 'var(--blue)' : 'transparent', fontSize: 11 }}>
          ✓
        </span>
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        {llm && (
          <span style={{ fontSize: 10, color: 'var(--blue)' }} title={t('cards.llmCategorized')}>
            ✦
          </span>
        )}
      </button>
      {(onRename || onDelete) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          className="mono"
          title={t('cards.frameMenu')}
          style={{
            fontSize: 11,
            padding: '0 10px',
            background: 'transparent',
            border: 'none',
            borderRadius: 3,
            cursor: 'pointer',
            color: 'var(--ink-3)',
            fontWeight: 700,
          }}
        >
          ⋯
        </button>
      )}
      {menuOpen && (
        <div
          onMouseLeave={() => setMenuOpen(false)}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 60,
            marginTop: 2,
            background: 'white',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            padding: 4,
            minWidth: 140,
          }}
        >
          {onRename && (
            <button
              onClick={() => {
                setMenuOpen(false);
                onRename();
              }}
              className="mono"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                fontSize: 11.5,
                padding: '6px 10px',
                color: 'var(--ink-1)',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                letterSpacing: '.04em',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {t('cards.rename')}
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => {
                setMenuOpen(false);
                onDelete();
              }}
              className="mono"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                fontSize: 11.5,
                padding: '6px 10px',
                color: 'var(--red)',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                letterSpacing: '.04em',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--red-soft)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {t('cards.deleteFrame')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyFlatView({
  cards, selectedId, highlightId, hoverHighlightIds, onHoverRefs, knownIds, onSelect, onPeek, onAdd, onShowHistory,
}: {
  cards: Card[];
  selectedId: string | null;
  highlightId: string | null;
  hoverHighlightIds?: ReadonlySet<string>;
  onHoverRefs?: (refs: string[]) => void;
  knownIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onPeek: (id: string, anchor: DOMRect) => void;
  onAdd: () => void;
  onShowHistory?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  if (cards.length === 0) {
    return (
      <div style={{ padding: '40px 22px', color: 'var(--ink-3)' }}>
        <div className="serif" style={{ fontSize: 18, color: 'var(--ink-2)', marginBottom: 6, fontStyle: 'normal' }}>
          {t('cards.cardsLandHere')}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.65 }}>
          {t('cards.cardsLandHereBody')}
        </div>
      </div>
    );
  }

  const ready = cards.length >= 6;
  return (
    <div style={{ padding: '14px 18px 0' }}>
      {/* Threshold banner */}
      <div
        style={{
          padding: '14px 16px',
          background: ready ? 'var(--blue-soft)' : 'var(--paper-1)',
          border: '1px solid',
          borderColor: ready ? 'var(--blue)' : 'var(--rule)',
          borderRadius: 6,
          marginBottom: 16,
        }}
      >
        <div className="mono" style={{ fontSize: 10, color: ready ? 'var(--blue)' : 'var(--ink-3)', letterSpacing: '.1em', fontWeight: 700, marginBottom: 6 }}>
          {ready ? `✦ ${t('cards.readyToOrganize')}` : t('cards.collecting')}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>
          {ready
            ? t('cards.readyBody', { count: cards.length })
            : t('cards.collectingBody', { count: cards.length })}
        </div>
        <button
          onClick={onAdd}
          disabled={!ready}
          className="mono"
          style={{
            marginTop: 10,
            fontSize: 11,
            padding: '6px 12px',
            borderRadius: 4,
            background: ready ? 'var(--ink-0)' : 'var(--ink-4)',
            color: 'white',
            border: 'none',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: ready ? 'pointer' : 'not-allowed',
          }}
        >
          + {t('cards.addAFrame')}
        </button>
      </div>

      {/* Flat card list */}
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.1em', fontWeight: 700, marginBottom: 8 }}>
        {t('cards.allCards')} · {cards.length}
      </div>
      {cards.map((c) => (
        <CardRow
          key={c.card_id}
          card={c}
          overridden={false}
          selected={c.card_id === selectedId}
          flash={c.card_id === highlightId}
          highlighted={hoverHighlightIds?.has(c.card_id) ?? false}
          knownIds={knownIds}
          onSelect={onSelect}
          onPeek={onPeek}
          onHoverRefs={onHoverRefs}
          onShowHistory={onShowHistory}
        />
      ))}
    </div>
  );
}

/**
 * Plain flat list — what shows when frames exist but the user picked the
 * "All cards" lens. No threshold banner (they're past the bootstrap stage),
 * no override semantics (no frame to override against).
 *
 * Supports a tiny `groupBy` axis:
 *   - 'none' → flat list, sorted newest-first by `created_at` (lens MVP default).
 *     Stable under card mutations (created_at doesn't change), so the
 *     ordering stays put when an existing card is patched.
 *   - 'kind' → sectioned by epistemic kind in a fixed maturity order
 *     (decision → statement → gap → question → observation → note). Within
 *     each section, also newest-first.
 *
 * No LLM, no recompute, no cache state: pure SQL output + a sort + a
 * group-by. The whole point of the lens MVP is to give users a view that
 * never drifts.
 */

/** Maturity-ordered kind list. Decisions surface first because that's
 *  what users want to see when scanning "what's settled". The tail
 *  (observation/note) is supporting matter. Order matches Kanban-style
 *  "done → in flight → todo → evidence → stub".
 *
 *  `question` is intentionally absent: questions render in the OPEN
 *  column's OPEN QUESTIONS section now, not in the cards list. (`gap`
 *  also rarely shows here — gaps live in OPEN's NEEDS YOUR CALL section
 *  — but it stays in the order in case a transient gap leaks through
 *  before SSE reconciliation moves it.)
 */
export const KIND_GROUP_ORDER: string[] = [
  'decision',
  'statement',
  'gap',
  'observation',
  'note',
];

function FlatList({
  cards, groupBy = 'none', selectedId, highlightId, hoverHighlightIds, onHoverRefs, knownIds, onSelect, onPeek, onShowHistory,
}: {
  cards: Card[];
  /** Single sort/group axis. See module docstring above. */
  groupBy?: 'none' | 'kind';
  selectedId: string | null;
  highlightId: string | null;
  hoverHighlightIds?: ReadonlySet<string>;
  onHoverRefs?: (refs: string[]) => void;
  knownIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onPeek: (id: string, anchor: DOMRect) => void;
  onShowHistory?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  if (cards.length === 0) {
    return (
      <div style={{ padding: '40px 22px', color: 'var(--ink-3)', fontSize: 12.5 }}>
        {t('cards.noCardsYet')}
      </div>
    );
  }

  // Stable order: created_at DESC, then card_id for deterministic tie-break
  // (rare collisions on ms timestamps in tests / fast bursts).
  const sorted = [...cards].sort((a, b) => {
    const cmp = (b.created_at ?? '').localeCompare(a.created_at ?? '');
    return cmp !== 0 ? cmp : b.card_id.localeCompare(a.card_id);
  });

  if (groupBy === 'none') {
    return (
      <div style={{ padding: '14px 18px 0' }}>
        {sorted.map((c) => (
          <CardRow
            key={c.card_id}
            card={c}
            overridden={false}
            selected={c.card_id === selectedId}
            flash={c.card_id === highlightId}
            highlighted={hoverHighlightIds?.has(c.card_id) ?? false}
            knownIds={knownIds}
            onSelect={onSelect}
            onPeek={onPeek}
            onHoverRefs={onHoverRefs}
            onShowHistory={onShowHistory}
          />
        ))}
      </div>
    );
  }

  // groupBy === 'kind' — bucket by epistemic kind, render section headers.
  // Sections are ordered by KIND_GROUP_ORDER; any kind not in that list
  // (e.g. legacy 'fact' / 'claim' from earlier schemas) falls through to
  // the tail in the order it first appears, so nothing gets silently
  // dropped.
  const buckets = new Map<string, Card[]>();
  for (const c of sorted) {
    const arr = buckets.get(c.kind) ?? [];
    arr.push(c);
    buckets.set(c.kind, arr);
  }
  const orderedKinds: string[] = [
    ...KIND_GROUP_ORDER.filter((k) => buckets.has(k)),
    ...[...buckets.keys()].filter((k) => !KIND_GROUP_ORDER.includes(k)),
  ];

  return (
    <div style={{ padding: '8px 18px 0' }}>
      {orderedKinds.map((kind) => {
        const items = buckets.get(kind) ?? [];
        const spec = specForKind(kind);
        return (
          <div key={kind} style={{ marginTop: 14 }}>
            <div
              className="mono"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                fontSize: 10,
                letterSpacing: '.12em',
                color: spec.color,
                fontWeight: 700,
                margin: '0 0 6px 0',
                paddingBottom: 4,
                borderBottom: `1px solid ${spec.rule}`,
              }}
            >
              <span>{spec.glyph}</span>
              <span>{spec.label}</span>
              <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}>· {items.length}</span>
            </div>
            {items.map((c) => (
              <CardRow
                key={c.card_id}
                card={c}
                overridden={false}
                selected={c.card_id === selectedId}
                flash={c.card_id === highlightId}
                highlighted={hoverHighlightIds?.has(c.card_id) ?? false}
                knownIds={knownIds}
                onSelect={onSelect}
                onPeek={onPeek}
                onHoverRefs={onHoverRefs}
                onShowHistory={onShowHistory}
                /* Section header above already shows the kind glyph + label —
                   skip the per-card repeat. */
                compactKind
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DropZone({
  path, onDrop, children,
}: {
  path: string;
  onDrop: (cardId: string, path: string) => void;
  children: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setHover(false);
        const cardId = e.dataTransfer.getData('text/plain');
        if (cardId) onDrop(cardId, path);
      }}
      style={{
        background: hover ? 'var(--blue-soft)' : 'transparent',
        transition: 'background 80ms',
        borderRadius: 4,
      }}
    >
      {children}
    </div>
  );
}

function GenerateFooter({
  projectId, cardCount, onGenerate, generating, staleDocCount,
}: {
  /** Needed for the cards-count export click. Null only on the empty
   *  bootstrap screen; we hide the click affordance in that case. */
  projectId: string | null;
  cardCount: number;
  onGenerate: () => void;
  generating: boolean;
  /** When >0, render a small dot on the button — a saved doc has gone
   *  stale because one of its source cards drifted. Click to open the
   *  Documents modal, which surfaces the actual stale rows. */
  staleDocCount: number;
}) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportHover, setExportHover] = useState(false);
  const canExport = !!projectId && cardCount > 0 && !exporting;
  const exportClick = async () => {
    if (!projectId || !canExport) return;
    setExporting(true);
    try {
      await api.exportProject(projectId);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`${t('cards.exportFailed')}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(false);
    }
  };
  const buttonTitle =
    cardCount === 0
      ? t('cards.addCardsFirst')
      : staleDocCount > 0
        ? t('cards.staleDocsHint', { count: staleDocCount })
        : t('cards.generateDocHint');
  return (
    <div
      style={{
        height: 48,
        padding: '0 14px',
        borderTop: '1px solid var(--rule)',
        background: 'var(--paper-1)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <button
        onClick={onGenerate}
        disabled={generating || cardCount === 0}
        className="mono"
        style={{
          position: 'relative',
          fontSize: 11,
          padding: '6px 12px',
          borderRadius: 4,
          background: generating || cardCount === 0 ? 'var(--ink-4)' : 'var(--ink-0)',
          color: 'white',
          letterSpacing: '.04em',
          fontWeight: 600,
          cursor: generating || cardCount === 0 ? 'not-allowed' : 'pointer',
        }}
        title={buttonTitle}
      >
        {generating ? t('cards.generating') : t('cards.generateDoc')}
        {staleDocCount > 0 && (
          <span
            aria-label={t('cards.staleDocsAria', { count: staleDocCount })}
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 14,
              height: 14,
              padding: '0 4px',
              borderRadius: 7,
              background: 'var(--red, #d23f3f)',
              color: 'white',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
              boxShadow: '0 0 0 1.5px white',
            }}
          >
            {staleDocCount}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={exportClick}
        onMouseEnter={() => setExportHover(true)}
        onMouseLeave={() => setExportHover(false)}
        disabled={!canExport}
        className="mono"
        title={
          !projectId
            ? ''
            : cardCount === 0
              ? t('cards.nothingToExport')
              : exporting
                ? t('cards.buildingExport')
                : t('cards.exportHint')
        }
        style={{
          fontSize: 9.5,
          color: canExport && exportHover ? 'var(--ink-1)' : 'var(--ink-3)',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: canExport ? 'pointer' : 'default',
          textDecoration: canExport && exportHover ? 'underline' : 'none',
          textUnderlineOffset: 2,
        }}
      >
        {exporting
          ? t('cards.exporting')
          : `${t('cards.cardCount', { count: cardCount })}${canExport && exportHover ? ' ⤓' : ''}`}
      </button>
    </div>
  );
}

function UnplacedSection({
  frameView, cards, selectedId, highlightId, hoverHighlightIds, onHoverRefs, knownIds, onSelect, onPeek, onDropToNode, onShowHistory, onResetOverride,
}: {
  frameView: FrameView | null;
  cards: Card[];
  selectedId: string | null;
  highlightId: string | null;
  hoverHighlightIds?: ReadonlySet<string>;
  onHoverRefs?: (refs: string[]) => void;
  knownIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onPeek: (id: string, anchor: DOMRect) => void;
  onDropToNode: (cardId: string, nodePath: string) => void;
  onShowHistory?: (cardId: string) => void;
  onResetOverride?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const unassigned = useMemo(() => {
    if (!frameView) return [] as Array<{ card: Card; overridden: boolean }>;
    const cardById = new Map(cards.map((c) => [c.card_id, c]));
    const out: Array<{ card: Card; overridden: boolean }> = [];
    for (const [cid, p] of Object.entries(frameView.placements)) {
      if (p.node_path !== PATH_UNASSIGNED) continue;
      const card = cardById.get(cid);
      if (!card) continue;
      if (card.kind === 'question') continue; // questions live in OPEN now
      out.push({ card, overridden: p.override_by_user });
    }
    return out;
  }, [frameView, cards]);

  if (unassigned.length === 0) return null;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const cardId = e.dataTransfer.getData('text/plain');
        if (cardId) onDropToNode(cardId, PATH_UNASSIGNED);
      }}
      style={{
        marginTop: 22,
        padding: '8px 18px 12px',
        background: hover ? 'var(--blue-soft)' : 'transparent',
        borderTop: '1px dashed var(--rule)',
      }}
    >
      <div
        className="mono"
        style={{ fontSize: 10.5, letterSpacing: '.1em', fontWeight: 700, color: 'var(--ink-3)', marginBottom: 4 }}
      >
        {t('cards.unplaced')} · {unassigned.length}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', marginBottom: 8 }}>
        {t('cards.unplacedHint')}
      </div>
      {unassigned.map(({ card, overridden }) => (
        <CardRow
          key={card.card_id}
          card={card}
          overridden={overridden}
          selected={card.card_id === selectedId}
          flash={card.card_id === highlightId}
          highlighted={hoverHighlightIds?.has(card.card_id) ?? false}
          knownIds={knownIds}
          onSelect={onSelect}
          onPeek={onPeek}
          onHoverRefs={onHoverRefs}
          onShowHistory={onShowHistory}
          onResetOverride={onResetOverride}
        />
      ))}
    </div>
  );
}

/**
 * Hidden cards — cards placed in `__excluded__`. These normally aren't
 * rendered at all; the user opts in via the lens picker's "View hidden"
 * toggle. Drag a hidden card onto any node to un-hide. Click the pin to
 * reset the override and let the LLM re-place it.
 *
 * Visually toned down (paper-1 background, italic helper text) to signal
 * "you're looking inside the hidden bin, not your normal view".
 */
function HiddenSection({
  frameView, cards, selectedId, highlightId, hoverHighlightIds, onHoverRefs, knownIds, onSelect, onPeek, onDropToNode, onShowHistory, onResetOverride,
}: {
  frameView: FrameView | null;
  cards: Card[];
  selectedId: string | null;
  highlightId: string | null;
  hoverHighlightIds?: ReadonlySet<string>;
  onHoverRefs?: (refs: string[]) => void;
  knownIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onPeek: (id: string, anchor: DOMRect) => void;
  onDropToNode: (cardId: string, nodePath: string) => void;
  onShowHistory?: (cardId: string) => void;
  onResetOverride?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const hidden = useMemo(() => {
    if (!frameView) return [] as Array<{ card: Card; overridden: boolean }>;
    const cardById = new Map(cards.map((c) => [c.card_id, c]));
    const out: Array<{ card: Card; overridden: boolean }> = [];
    for (const [cid, p] of Object.entries(frameView.placements)) {
      if (p.node_path !== PATH_EXCLUDED) continue;
      const card = cardById.get(cid);
      if (!card) continue;
      if (card.kind === 'question') continue; // questions live in OPEN now
      out.push({ card, overridden: p.override_by_user });
    }
    return out;
  }, [frameView, cards]);

  if (hidden.length === 0) return null;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const cardId = e.dataTransfer.getData('text/plain');
        if (cardId) onDropToNode(cardId, PATH_EXCLUDED);
      }}
      style={{
        marginTop: 22,
        padding: '8px 18px 12px',
        background: hover ? 'var(--blue-soft)' : 'var(--paper-1)',
        borderTop: '1px dashed var(--rule)',
      }}
    >
      <div
        className="mono"
        style={{ fontSize: 10.5, letterSpacing: '.1em', fontWeight: 700, color: 'var(--ink-3)', marginBottom: 4 }}
      >
        {t('cards.hidden')} · {hidden.length}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', marginBottom: 8 }}>
        {t('cards.hiddenHint')}
      </div>
      {hidden.map(({ card, overridden }) => (
        <CardRow
          key={card.card_id}
          card={card}
          overridden={overridden}
          selected={card.card_id === selectedId}
          flash={card.card_id === highlightId}
          highlighted={hoverHighlightIds?.has(card.card_id) ?? false}
          knownIds={knownIds}
          onSelect={onSelect}
          onPeek={onPeek}
          onHoverRefs={onHoverRefs}
          onShowHistory={onShowHistory}
          onResetOverride={onResetOverride}
        />
      ))}
    </div>
  );
}

// ── tree builder ──────────────────────────────────────────────────

function buildSections(view: FrameView | null, allCards: Card[]): SectionTree[] {
  if (!view) return [];

  // Map carries every card (incl. questions) so cardById lookups stay
  // complete — wikilinks inside other cards' bodies still resolve. We
  // skip kind=question only when populating the rendered buckets below
  // (questions live in OPEN now; their frame placements, if any, are
  // preserved server-side but invisible here).
  const cardById = new Map<string, Card>();
  for (const c of allCards) cardById.set(c.card_id, c);

  // Bucket: path → cards.
  const bucket = new Map<string, Array<{ card: Card; overridden: boolean }>>();
  for (const [cardId, p] of Object.entries(view.placements)) {
    if (p.node_path === PATH_EXCLUDED) continue;
    if (p.node_path === PATH_UNASSIGNED) continue; // handled separately
    const card = cardById.get(cardId);
    if (!card) continue;
    if (card.kind === 'question') continue; // questions live in OPEN now
    if (!bucket.has(p.node_path)) bucket.set(p.node_path, []);
    bucket.get(p.node_path)!.push({ card, overridden: p.override_by_user });
  }

  // Group nodes by top-level (path with no slash). Children attach.
  const tops = view.frame.topology.filter((n) => !n.path.includes('/'));
  return tops.map((top) => {
    const childNodes = view.frame.topology.filter(
      (n) => n.path.startsWith(top.path + '/'),
    );
    const cardsAtSelf = bucket.get(top.path) ?? [];
    const subSections = childNodes.map((child) => ({
      node: child,
      cards: bucket.get(child.path) ?? [],
    }));
    return { topNode: top, cardsAtSelf, subSections };
  });
}

function countCardsInSection(sec: SectionTree): number {
  let n = sec.cardsAtSelf.length;
  for (const sub of sec.subSections) n += sub.cards.length;
  return n;
}

/** doc/12 §3.3.2 — act three of onboarding: the guided stale demo.
 *
 *  Eligibility: the project has a decision card that at least one other
 *  card depends on (body contains `→depends-on [[<decision>]]`), and the
 *  user has never seen a stale ripple (localStorage flag, set either by
 *  dismissing this chip or by App.tsx when a real cards_marked_stale
 *  event fires). Clicking opens the decision in CardPeek — the edit the
 *  user then makes triggers the ripple for real, which beats any amount
 *  of copy explaining it. */
const STALE_DEMO_KEY = 'draftlize:stale_demo_done';

function StaleDemoChip({
  cards,
  onPeek,
}: {
  cards: Card[];
  onPeek: (id: string, anchor: DOMRect) => void;
}) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STALE_DEMO_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  // Find a decision some other card depends on. Body-text scan mirrors
  // how edges are double-written (`→depends-on [[c-N]]` in the body).
  const decisionIds = new Set(
    cards.filter((c) => c.kind === 'decision').map((c) => c.card_id),
  );
  if (decisionIds.size === 0) return null;
  let target: string | null = null;
  for (const c of cards) {
    const m = c.body.matchAll(/→depends-on\s*\[\[([^\]]+)\]\]/g);
    for (const hit of m) {
      const id = hit[1].trim();
      if (decisionIds.has(id) && id !== c.card_id) {
        target = id;
        break;
      }
    }
    if (target) break;
  }
  if (!target) return null;
  const retire = () => {
    try {
      localStorage.setItem(STALE_DEMO_KEY, '1');
    } catch {
      /* private mode — chip may reappear next session, harmless */
    }
    setDismissed(true);
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: '8px 10px 0',
        padding: '8px 12px',
        background: 'var(--amber-soft, #fdf3df)',
        border: '1px solid var(--amber, #d9a440)',
        borderRadius: 6,
        fontSize: 12.5,
        lineHeight: 1.45,
        color: 'var(--ink-1)',
        flexShrink: 0,
      }}
    >
      <span aria-hidden="true">⚡</span>
      <span style={{ flex: 1 }}>{t('cards.staleDemoPrompt')}</span>
      <button
        type="button"
        onClick={(e) => onPeek(target!, e.currentTarget.getBoundingClientRect())}
        className="mono"
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '.05em',
          padding: '4px 10px',
          border: '1px solid var(--ink-0)',
          borderRadius: 4,
          background: 'var(--ink-0)',
          color: 'white',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        {t('cards.staleDemoCta')}
      </button>
      <button
        type="button"
        onClick={retire}
        title={t('cards.staleDemoDismiss')}
        style={{
          border: 'none',
          background: 'transparent',
          color: 'var(--ink-3)',
          cursor: 'pointer',
          fontSize: 14,
          padding: '0 2px',
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
